import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useSelector } from 'react-redux';

import OffersService from '~/services/OffersService';
import PublicOffersService from '~/services/PublicOfferService';
import SearchService, { HotelSearchList } from '~/services/SearchService';

interface UseHotelOffersByPlaceIdsParams {
  placeIds: Array<string>;
  enabled?: boolean;
}

const STALE_TIME = 5 * 60 * 1000;

export interface HotelOffer {
  id: string;
  title: string;
  imageId?: string;
}

export function useHotelOffersByPlaceIds({ placeIds, enabled = true }: UseHotelOffersByPlaceIdsParams) {
  const { enqueueSnackbar } = useSnackbar();
  const brand = useSelector((state: App.State) => state.tenant.brand);
  const region = 'AU';

  const placeOffersQueries = useQueries({
    queries: placeIds.map((placeId) => ({
      queryKey: ['hotel-offers', 'by-place-id', placeId, brand, region],
      queryFn: async (): Promise<HotelSearchList> => {
        const result = await SearchService.getSearchLightOffers({
          region,
          brand: brand.toLowerCase(),
          destinationId: placeId,
          limit: 1000,
          noCache: true,
        });
        return result;
      },
      enabled: enabled && !!placeId,
      staleTime: STALE_TIME,
    })),
  });

  const allOfferIds = useMemo(() => {
    const offerIdSet = new Set<string>();
    placeOffersQueries.forEach((query) => {
      if (query.data) {
        query.data.forEach((offer) => {
          offerIdSet.add(offer.id);
        });
      }
    });
    return Array.from(offerIdSet);
  }, [placeOffersQueries]);

  const offerDetailsQueries = useQueries({
    queries: allOfferIds.map((offerId) => ({
      queryKey: ['hotel-offer-details', offerId, brand],
      queryFn: async (): Promise<HotelOffer | null> => {
        try {
          const isBedbank = offerId.length === 36;
          if (isBedbank) {
            const response = await PublicOffersService.getBedbankOfferById(offerId, region, brand);
            return {
              id: offerId,
              title: response.result.name || offerId,
              imageId: response.result.images?.[0]?.id,
            };
          } else {
            const response = await OffersService.getOffer(offerId);
            return {
              id: offerId,
              title: response.result.name,
              imageId: response.result.images?.[0]?.id_cloudinary_external,
            };
          }
        } catch (error) {
          enqueueSnackbar(`Error fetching offer details for ${offerId}: ${error.message}`, { variant: 'error' });
          return null;
        }
      },
      enabled: enabled && !!offerId,
      staleTime: STALE_TIME,
    })),
  });

  const offers = useMemo(() => {
    return offerDetailsQueries
      .map((query) => query.data)
      .filter((offer): offer is HotelOffer => offer !== null && offer !== undefined);
  }, [offerDetailsQueries]);

  const isLoading =
    placeOffersQueries.some((query) => query.isLoading) || offerDetailsQueries.some((query) => query.isLoading);
  const isError =
    placeOffersQueries.some((query) => query.isError) || offerDetailsQueries.some((query) => query.isError);

  return {
    offers,
    isLoading,
    isError,
    allOfferIds,
  };
}
